// First-Time Google Login Onboarding & Profile Editing Modal (Dad Resume Setup)
import { getCurrentProfile, updateDadProfile } from '../services/authService';
import { showToast } from '../components/Toast';

export function renderOnboardingModal(): string {
  const profile = getCurrentProfile();
  if (!profile) return '';

  return `
    <div id="modal-onboarding" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in font-sans">
      <div class="bg-[#0a0c13] border border-cyan-400/40 shadow-[0_0_35px_rgba(0,240,255,0.15)] rounded-2xl max-w-lg w-full p-6 space-y-4 my-8 relative overflow-hidden">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="border-b border-white/[0.08] pb-3 text-center space-y-1 relative">
          ${profile.onboarded ? `
            <button id="close-onboarding-modal-btn" class="absolute right-0 top-0 text-slate-400 hover:text-white p-1 text-sm cursor-pointer transition-colors" title="Close">
              <i class="fa-solid fa-xmark text-base"></i>
            </button>
          ` : ''}
          <div class="w-12 h-12 rounded-2xl bg-transparent border border-cyan-400/50 text-cyan-400 flex items-center justify-center text-xl font-black mx-auto mb-2 shadow-[0_0_15px_rgba(0,240,255,0.2)]">
            <i class="fa-solid fa-id-card"></i>
          </div>
          <h3 class="text-lg font-black text-slate-100 tracking-tight">
            ${profile.onboarded ? 'Edit Dad Resume' : 'Welcome to Pops Culture!'}
          </h3>
          <p class="text-xs text-slate-400">
            ${profile.onboarded ? 'Update your private profile and public display preferences.' : 'Set up your Dad Resume so neighbors know how you can pitch in.'}
          </p>
        </div>

        <form id="onboarding-form" class="space-y-3.5 text-xs">
          
          <!-- Immutable OAuth Credentials Banner -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl bg-[#06070a] border border-white/[0.08]">
            <div class="space-y-1">
              <label class="block font-bold text-slate-300 flex items-center justify-between text-[11px]">
                <span>Google Name</span>
                <span class="text-[9px] text-cyan-400 font-normal flex items-center gap-1"><i class="fa-brands fa-google"></i> Verified</span>
              </label>
              <input type="text" id="onboard-name-disabled" value="${profile.name}" disabled class="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-slate-400 text-xs cursor-not-allowed select-none">
              <span class="text-[9px] text-slate-500 block">Locked to Google OAuth</span>
            </div>

            <div class="space-y-1">
              <label class="block font-bold text-slate-300 flex items-center justify-between text-[11px]">
                <span>Google Email</span>
                <span class="text-[9px] text-cyan-400 font-normal flex items-center gap-1"><i class="fa-solid fa-lock"></i> Account ID</span>
              </label>
              <input type="email" id="onboard-email-disabled" value="${profile.email}" disabled class="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-slate-400 text-xs cursor-not-allowed select-none">
              <span class="text-[9px] text-slate-500 block">Private &bull; Admin reporting only</span>
            </div>
          </div>

          <!-- Display Name (Optional) & Neighborhood Base -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="block font-bold text-slate-200">
                Display Name <span class="text-slate-400 font-normal">(Optional)</span>
              </label>
              <input type="text" id="onboard-display-name" value="${profile.displayName || ''}" placeholder="e.g. Coach Dave, Big Mike" class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
              <span class="text-[10px] text-slate-400 block">Leave blank to display: <strong>${profile.name}</strong></span>
            </div>
            <div class="space-y-1">
              <label class="block font-bold text-slate-200">Neighborhood Base *</label>
              <input type="text" id="onboard-neighborhood" value="${profile.neighborhood}" required placeholder="e.g. North Ward, Oak Ridge" class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
              <span class="text-[10px] text-slate-400 block">Private to your account</span>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Years as a Father</label>
              <input type="number" id="onboard-years" value="${profile.yearsAsDad || 1}" min="0" max="60" class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
            </div>
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Preferred Beverage</label>
              <input type="text" id="onboard-beverage" value="${profile.beverage}" placeholder="e.g. Unsweetened Iced Tea & Cold IPA" class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
            </div>
          </div>

          <div class="space-y-1">
            <label class="block font-bold text-slate-300">Dad Bio & Vibe</label>
            <textarea id="onboard-bio" rows="2" placeholder="Tell us about yourself and what projects you enjoy." class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">${profile.bio}</textarea>
          </div>

          <div class="space-y-1">
            <label class="block font-bold text-slate-300">Hobby Skills (comma-separated)</label>
            <input type="text" id="onboard-skills" value="${profile.skills.join(', ')}" placeholder="e.g. Carpentry, Grilling, Lawn Care, Electrical" class="w-full bg-[#06070a] border border-white/[0.12] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
          </div>

          <!-- Privacy Protection Notice -->
          <div class="p-3 rounded-xl bg-[#06070a] border border-white/[0.08] text-[11px] text-slate-400 space-y-1">
            <div class="flex items-center gap-1.5 text-cyan-400 font-bold">
              <i class="fa-solid fa-user-shield"></i> Member Privacy Safeguard
            </div>
            <p class="leading-relaxed">
              Only your <strong>Name</strong> (or custom Display Name) is visible to other dads in the community. All other details (email, neighborhood, bio, and skills) remain private to you and internal admin moderation tools.
            </p>
          </div>

          <div class="pt-2 border-t border-white/[0.08] flex justify-end">
            <button type="submit" class="btn-primary w-full py-2.5 rounded-xl font-bold text-xs tracking-wider uppercase">
              ${profile.onboarded ? 'Save Changes' : 'Complete Dad Resume & Go to the App'}
            </button>
          </div>

        </form>

      </div>
    </div>
  `;
}

export function bindOnboardingModalEvents(onComplete: () => void, onClose?: () => void): void {
  const closeBtn = document.getElementById('close-onboarding-modal-btn');
  closeBtn?.addEventListener('click', () => {
    if (onClose) onClose();
    else onComplete();
  });

  const form = document.getElementById('onboarding-form') as HTMLFormElement;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const displayName = (document.getElementById('onboard-display-name') as HTMLInputElement).value.trim();
    const neighborhood = (document.getElementById('onboard-neighborhood') as HTMLInputElement).value.trim();
    const yearsAsDad = parseInt((document.getElementById('onboard-years') as HTMLInputElement).value, 10) || 1;
    const beverage = (document.getElementById('onboard-beverage') as HTMLInputElement).value.trim();
    const bio = (document.getElementById('onboard-bio') as HTMLTextAreaElement).value.trim();
    const skillsRaw = (document.getElementById('onboard-skills') as HTMLInputElement).value.trim();

    const skills = skillsRaw ? skillsRaw.split(',').map(s => s.trim()).filter(Boolean) : ['General Handyman / Lifting'];

    // Note: Name and Email are strictly omitted here to preserve authoritative Google OAuth values
    await updateDadProfile({
      displayName: displayName || '',
      neighborhood,
      yearsAsDad,
      beverage: beverage || 'Cold Beverage',
      bio: bio || 'Ready to pitch in and meet fellow neighborhood dads.',
      skills,
      onboarded: true
    });

    showToast('Dad Resume updated! Welcome aboard.');
    onComplete();
  });
}
